import { createStorage } from '@aviata/storage';
import { locales, type Locale } from './config';

const localeStorage = createStorage(localStorage);
const localeStorageKey = 'locale';

const getLocaleFromStorage = () => {
  return localeStorage.get(localeStorageKey) as Locale | null;
};

const isSupportedLocale = (locale: string): locale is Locale =>
  Object.values(locales).includes(locale as Locale);

/**
 * Method that return the native app selected locale.
 * Native app sets query param "language" with selected locale
 */
export const getAppLocale = (fallbackLocale: Locale = locales.RU): Locale => {
  const { search } = document.location;
  const searchParams = new URLSearchParams(search);
  const queryLocale = searchParams.get('language');

  if (queryLocale && isSupportedLocale(queryLocale)) {
    localeStorage.set(localeStorageKey, queryLocale);
    return queryLocale;
  }

  const localeFromStorage = getLocaleFromStorage();

  if (localeFromStorage) {
    return localeFromStorage;
  }

  return fallbackLocale;
};

/**
 * Method returns index of a word to use in plural form for vue-i18n
 * @see https://vue-i18n.intlify.dev/guide/essentials/pluralization.html#custom-pluralization
 * @param choice - number of elements
 * @param choicesLength - total number of plural forms of an element
 * @returns index of word to use in plural form for i18n
 */
export const getRussianPluralFormRules = (
  choice: number,
  choicesLength: number,
) => {
  if (choice === 0) {
    return 0;
  }

  const teen = choice > 10 && choice < 20;
  const endsWithOne = choice % 10 === 1;
  if (!teen && endsWithOne) {
    return 1;
  }
  if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
    return 2;
  }

  return choicesLength < 4 ? 2 : 3;
};
